import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../hooks/useAuth.jsx';

const { FiUsers, FiFileText, FiShield, FiHome, FiBriefcase, FiTrendingUp, FiDollarSign, FiCode, FiAward, FiMonitor, FiBookOpen, FiBuilding, FiMapPin, FiKey, FiArrowRight, FiArrowLeft } = FiIcons;

const TemplatesPage = ({ onAuthRequired }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const categories = [
    {
      name: 'Partnership Agreements',
      type: 'partnership',
      description: 'Founders, joint ventures, and business partnerships',
      templates: [
        {
          id: 'llc_operating',
          title: 'LLC Operating Agreement',
          description: 'Operating structure for multi-member LLC businesses',
          icon: FiFileText,
          color: 'from-green-500 to-green-600',
          features: ['Member interests', 'Management structure', 'Capital calls', 'Dissolution terms'],
          estimatedTime: '20-25 min'
        },
        {
          id: 'joint_venture',
          title: 'Joint Venture Agreement',
          description: 'Collaboration between two or more businesses or individuals',
          icon: FiTrendingUp,
          color: 'from-purple-500 to-purple-600',
          features: ['Profit sharing', 'Decision making', 'Contribution terms', 'Termination rights'],
          estimatedTime: '25-30 min'
        },
        {
          id: 'investor_founder',
          title: 'Investor-Founder Agreement',
          description: 'Terms for bringing investors into your partnership',
          icon: FiDollarSign,
          color: 'from-yellow-500 to-yellow-600',
          features: ['Investment terms', 'Board seats', 'Milestones', 'Liquidation preferences'],
          estimatedTime: '30-35 min'
        },
        {
          id: 'professional_services',
          title: 'Professional Services Partnership',
          description: 'For lawyers, accountants, and other professional service firms',
          icon: FiBriefcase,
          color: 'from-indigo-500 to-indigo-600',
          features: ['Client responsibility', 'Profit distribution', 'Retirement terms', 'Admission of new partners'],
          estimatedTime: '20-25 min'
        }
      ]
    },
    {
      name: 'Contractor Services',
      type: 'contractor',
      description: 'Freelancers, consultants, and independent contractors',
      templates: [
        {
          id: 'consulting_services',
          title: 'Consulting Services',
          description: 'Specialized consulting and advisory services agreement',
          icon: FiBriefcase,
          color: 'from-green-500 to-green-600',
          features: ['Consulting scope', 'Deliverables', 'Retainer terms', 'Performance metrics'],
          estimatedTime: '15-20 min'
        },
        {
          id: 'software_development',
          title: 'Software Development',
          description: 'Contract for custom software and app development',
          icon: FiCode,
          color: 'from-purple-500 to-purple-600',
          features: ['Technical specs', 'Milestones', 'Source code rights', 'Support terms'],
          estimatedTime: '20-25 min'
        },
        {
          id: 'marketing_services',
          title: 'Marketing Services',
          description: 'Digital marketing and advertising service agreements',
          icon: FiTrendingUp,
          color: 'from-yellow-500 to-yellow-600',
          features: ['Campaign scope', 'Performance KPIs', 'Budget approval', 'Content ownership'],
          estimatedTime: '15-20 min'
        },
        {
          id: 'construction_contract',
          title: 'Construction Contract',
          description: 'General construction and renovation services agreement',
          icon: FiHome,
          color: 'from-indigo-500 to-indigo-600',
          features: ['Project specifications', 'Timeline', 'Change orders', 'Warranty terms'],
          estimatedTime: '25-30 min'
        }
      ]
    },
    {
      name: 'Employment Contracts',
      type: 'employment',
      description: 'Employees, executives, and workplace agreements',
      templates: [
        {
          id: 'executive_employment',
          title: 'Executive Employment',
          description: 'High-level executive and C-suite employment agreements',
          icon: FiAward,
          color: 'from-blue-500 to-blue-600',
          features: ['Executive compensation', 'Severance package', 'Change of control', 'Performance bonuses'],
          estimatedTime: '30-35 min'
        },
        {
          id: 'sales_commission',
          title: 'Sales Commission',
          description: 'Sales representative and commission-based employment',
          icon: FiDollarSign,
          color: 'from-green-500 to-green-600',
          features: ['Commission structure', 'Territory rights', 'Draw vs. commission', 'Termination clauses'],
          estimatedTime: '20-25 min'
        },
        {
          id: 'remote_work',
          title: 'Remote Work Agreement',
          description: 'Work-from-home and remote employment policies',
          icon: FiMonitor,
          color: 'from-purple-500 to-purple-600',
          features: ['Work location', 'Equipment provision', 'Communication', 'Performance monitoring'],
          estimatedTime: '15-20 min'
        },
        {
          id: 'internship_agreement',
          title: 'Internship Agreement',
          description: 'Unpaid or paid internship and training agreements',
          icon: FiBookOpen,
          color: 'from-yellow-500 to-yellow-600',
          features: ['Learning objectives', 'Duration', 'Stipend terms', 'IP assignment'],
          estimatedTime: '10-15 min'
        }
      ]
    },
    {
      name: 'Real Estate Agreements',
      type: 'real_estate',
      description: 'Leasing, purchasing, and property management',
      templates: [
        {
          id: 'commercial_lease',
          title: 'Commercial Lease',
          description: 'Office and retail space leasing agreements',
          icon: FiBuilding,
          color: 'from-green-500 to-green-600',
          features: ['Rent structure', 'Tenant improvements', 'Operating expenses', 'Assignment rights'],
          estimatedTime: '30-35 min'
        },
        {
          id: 'property_purchase',
          title: 'Property Purchase',
          description: 'Real estate purchase and sale agreements',
          icon: FiMapPin,
          color: 'from-purple-500 to-purple-600',
          features: ['Purchase price', 'Contingencies', 'Closing terms', 'Warranties'],
          estimatedTime: '35-40 min'
        },
        {
          id: 'property_management',
          title: 'Property Management',
          description: 'Property management and landlord services',
          icon: FiKey,
          color: 'from-yellow-500 to-yellow-600',
          features: ['Management fees', 'Service scope', 'Reporting requirements', 'Termination terms'],
          estimatedTime: '20-25 min'
        },
        {
          id: 'sublease_agreement',
          title: 'Sublease Agreement',
          description: 'Subletting existing leased premises',
          icon: FiArrowRight,
          color: 'from-indigo-500 to-indigo-600',
          features: ['Sublease terms', 'Rent structure', 'Consent requirements', 'Liability'],
          estimatedTime: '15-20 min'
        }
      ]
    }
  ];

  const handleTemplateSelect = (type) => {
    if (!user) {
      onAuthRequired('signin');
      return;
    }
    navigate(`/wizard/${type}`);
  };

  return (
    <div className="min-h-screen pt-20 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
            Choose Your Template
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Start with our AI-guided templates for common business agreements.
            Each template is customized to your specific situation.
          </p>
        </motion.div>

        {/* Categories */}
        {categories.map((category, categoryIndex) => (
          <motion.div
            key={category.type}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-8">
              <h2 className="text-3xl font-bold text-slate-800">{category.name}</h2>
              <div className="flex-1 h-px bg-gradient-to-r from-slate-300 to-transparent"></div>
            </div>
            <p className="text-lg text-slate-600 mb-8">{category.description}</p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* First template - highlighted as primary */}
              <motion.div
                onClick={() => handleTemplateSelect(category.type)}
                className="glass-effect rounded-2xl p-8 cursor-pointer hover:shadow-glow transform hover:scale-105 transition-all duration-300 group col-span-full lg:col-span-1"
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${categories.find(c => c.type === category.type).templates[0].color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200`}>
                  <SafeIcon icon={FiUsers} className="text-2xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-4">Co-Founder Partnership</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">Fair equity agreements for startup founders and business partners</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-700">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                    <span className="text-sm">Equity splits</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                    <span className="text-sm">Vesting schedules</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                    <span className="text-sm">Exit clauses</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                    <span className="text-sm">IP ownership</span>
                  </li>
                </ul>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-600">
                    <SafeIcon icon={FiShield} className="inline mr-1" />
                    15-20 min
                  </div>
                  <div className="flex items-center gap-2 text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                    Start Creating
                    <SafeIcon icon={FiArrowLeft} />
                  </div>
                </div>
              </motion.div>

              {/* Other templates in category */}
              {category.templates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onClick={() => handleTemplateSelect(category.type)}
                  className="glass-effect rounded-2xl p-8 cursor-pointer hover:shadow-glow transform hover:scale-105 transition-all duration-300 group"
                >
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${template.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200`}>
                    <SafeIcon icon={template.icon} className="text-2xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">{template.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{template.description}</p>
                  <ul className="space-y-2 mb-6">
                    {template.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-slate-700">
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      <SafeIcon icon={FiShield} className="inline mr-1" />
                      {template.estimatedTime}
                    </div>
                    <div className="flex items-center gap-2 text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                      Start Creating
                      <SafeIcon icon={FiArrowLeft} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">
              Don't see what you need?
            </h2>
            <p className="text-slate-600 mb-6">
              Our AI can help customize any agreement type or create something entirely new.
              Start with one of our templates and modify as needed.
            </p>
            <button
              onClick={() => navigate('/wizard/partnership')}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Create Custom Agreement
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TemplatesPage;